import React, { useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  CircleCheck,
  Handshake,
  Linkedin,
  MessageSquareText,
  Radar,
  Repeat,
  Tag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const WEAVE_CLUB_URL = 'https://www.wearetheweave.co.uk/the-1-club';
const WEAVE_LINKEDIN_URL = 'https://www.linkedin.com/company/wearetheweave/';

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: [0.2, 0.8, 0.2, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- Nav ---------- */

const Nav = ({ onScanClick }) => (
  <nav className="bg-cream-50 relative z-30 border-b border-ink/5" data-testid="site-nav">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 md:gap-5 min-w-0">
        <a href="/" className="flex items-center" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_aligned-network/artifacts/a37uk3vc_WEAVE-LOGO%202024.png"
            alt="The Weave"
            className="h-10 w-auto"
          />
        </a>
        <span className="hidden md:inline-block w-px h-7 bg-ink/15" />
        <span className="hidden md:inline eyebrow truncate">The 1% Club &middot; The Aligned Network</span>
      </div>
      <div className="flex items-center gap-3 md:gap-6 shrink-0">
        <a
          href={WEAVE_CLUB_URL}
          target="_blank"
          rel="noreferrer"
          className="hidden sm:inline text-sm text-ink-soft hover:text-ink underline-wave"
          data-testid="nav-join-cta"
        >
          Join The 1% Club
        </a>
        <button onClick={onScanClick} className="cta-red" data-testid="nav-scan-cta">
          Get my free read <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </nav>
);

/* ---------- Hero ---------- */

const Hero = ({ onScanClick }) => (
  <section className="relative overflow-hidden bg-weave-deep text-cream-50" data-testid="hero">
    <div className="absolute inset-0 bg-grid pointer-events-none" />
    <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-weave-teal/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
      <Reveal>
        <div className="text-[11px] uppercase tracking-[0.22em] text-cream-100/55 font-medium">
          The 1% Club &middot; The Aligned Network
        </div>
      </Reveal>
      <Reveal delay={0.05}>
        <h1 className="font-sans font-black tracking-tight text-cream-50 mt-7 text-[44px] leading-[1.0] md:text-[88px] md:leading-[0.96] lg:text-[100px]">
          An <span className="text-weave-yellow wave-yellow">honest</span> read of your LinkedIn.
          <br className="hidden md:block" />
          A practice that compounds it.
        </h1>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 text-base md:text-lg text-cream-100/85 max-w-2xl leading-relaxed">
          A free AI appraisal of your personal and business presence — alignment, activity,
          engagement, visibility. Plus 5&middot;5&middot;5, the daily practice we use inside The Weave to turn LinkedIn into real relationships. The Weave Networker — our extension that makes it compound — is part of The 1% Club.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button onClick={onScanClick} className="cta-red" data-testid="hero-scan-cta">
            Get my free read <ArrowRight className="h-4 w-4" />
          </button>
          <a
            href={WEAVE_CLUB_URL}
            target="_blank"
            rel="noreferrer"
            className="cta-outline"
            data-testid="hero-join-cta"
          >
            Join The 1% Club
          </a>
        </div>
        <p className="mt-4 text-xs text-cream-100/55">Free. No card. Just signal.</p>
      </Reveal>
      <Reveal delay={0.2}>
        <hr className="border-cream-100/15 mt-14 mb-7" />
        <div className="flex flex-wrap gap-x-10 md:gap-x-14 gap-y-4 items-center" data-testid="hero-pillars">
          <span className="text-xl md:text-2xl font-medium tracking-tight inline-flex items-center gap-2">
            <span className="text-weave-yellow text-2xl leading-none">&bull;</span> Alignment
          </span>
          <span className="text-xl md:text-2xl font-medium tracking-tight inline-flex items-center gap-2">
            <span className="text-weave-teal text-2xl leading-none">&bull;</span> Activity
          </span>
          <span className="text-xl md:text-2xl font-medium tracking-tight inline-flex items-center gap-2">
            <span className="text-weave-red text-2xl leading-none">&bull;</span> Engagement
          </span>
          <span className="text-xl md:text-2xl font-medium tracking-tight inline-flex items-center gap-2">
            <span className="text-cream-50 text-2xl leading-none">&bull;</span> Visibility
          </span>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- How it works ---------- */

const HowItWorks = () => (
  <section className="section-pad" data-testid="how-it-works">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-4">
          <div className="eyebrow">How it works</div>
          <h2 className="serif text-4xl md:text-5xl leading-tight mt-3">
            Three steps. <br /> One honest read.
          </h2>
        </Reveal>
        <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              n: '01',
              t: 'Share your presence',
              b: 'Paste your personal headline, About, and company positioning. A note on how you post and engage.',
            },
            {
              n: '02',
              t: 'AI reads the alignment',
              b: 'We weigh alignment, activity, engagement and visibility — against how trusted founders actually build.',
            },
            {
              n: '03',
              t: 'You get the map',
              b: 'A score with one strength, one gap and one move per dimension — on screen and in your inbox.',
            },
          ].map((s, i) => (
            <Reveal key={s.n} delay={0.1 * i}>
              <div>
                <div className="idx">{s.n}</div>
                <h3 className="serif text-2xl leading-tight mt-3">{s.t}</h3>
                <p className="text-ink-soft mt-2 text-sm leading-relaxed">{s.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Diagnostic form ---------- */

const emptyForm = {
  full_name: '',
  email: '',
  personal_linkedin_url: '',
  personal_headline: '',
  personal_about: '',
  company_name: '',
  company_linkedin_url: '',
  company_about: '',
  activity_summary: '',
  goal: '',
  consent_terms: false,
  consent_marketing: false,
};

const DiagnosticSection = ({ formRef, reportRef, onReport, report }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_terms) {
      toast.error('Please confirm the consent checkbox to continue.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/diagnostic/analyze`, form, { timeout: 90000 });
      onReport(data);
      toast.success('Your read is ready — scroll down.');
      setTimeout(() => {
        reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'We couldn\'t generate the read. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={formRef} id="scan" className="section-pad bg-cream-100/70 border-y border-ink/10" data-testid="diagnostic-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="eyebrow">Your free read</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight mt-3">
              Hand us the signal.
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed">
              The sharper the input, the sharper the read. Four minutes. No card. No spam. The
              full report lands on screen and in your inbox.
            </p>
            <div className="mt-8 space-y-3 text-sm text-ink-soft">
              <div className="flex items-start gap-2"><CircleCheck className="h-4 w-4 mt-0.5 text-weave-teal" /><span>Alignment between you and your business</span></div>
              <div className="flex items-start gap-2"><CircleCheck className="h-4 w-4 mt-0.5 text-weave-teal" /><span>Posting style, tempo and mix</span></div>
              <div className="flex items-start gap-2"><CircleCheck className="h-4 w-4 mt-0.5 text-weave-teal" /><span>How you engage with others' work</span></div>
              <div className="flex items-start gap-2"><CircleCheck className="h-4 w-4 mt-0.5 text-weave-teal" /><span>Visibility & discoverability</span></div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-8" delay={0.12}>
            <form onSubmit={submit} className="grain-card p-6 md:p-8 space-y-6" data-testid="diagnostic-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <Input value={form.full_name} onChange={set('full_name')} required data-testid="input-full-name" placeholder="Jane Founder" />
                </Field>
                <Field label="Email" required>
                  <Input type="email" value={form.email} onChange={set('email')} required data-testid="input-email" placeholder="you@company.co" />
                </Field>
              </div>

              <Field label="Your personal LinkedIn URL" required>
                <Input value={form.personal_linkedin_url} onChange={set('personal_linkedin_url')} required data-testid="input-personal-url" placeholder="https://linkedin.com/in/…" />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your LinkedIn headline" hint="The line under your name.">
                  <Input value={form.personal_headline} onChange={set('personal_headline')} data-testid="input-personal-headline" placeholder="Founder, XYZ · helping…"/>
                </Field>
                <Field label="Company name" hint="Optional">
                  <Input value={form.company_name} onChange={set('company_name')} data-testid="input-company-name" placeholder="XYZ Co."/>
                </Field>
              </div>

              <Field label="Your LinkedIn About" hint="Paste the opening few lines (or whole thing). Optional but helps the read.">
                <Textarea rows={4} value={form.personal_about} onChange={set('personal_about')} data-testid="input-personal-about" placeholder="I help… / We build… / Currently…" />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Company LinkedIn URL" hint="Optional">
                  <Input value={form.company_linkedin_url} onChange={set('company_linkedin_url')} data-testid="input-company-url" placeholder="https://linkedin.com/company/…" />
                </Field>
                <Field label="Company tagline / About" hint="A sentence is fine.">
                  <Input value={form.company_about} onChange={set('company_about')} data-testid="input-company-about" placeholder="We help founders…" />
                </Field>
              </div>

              <Field
                label="How you show up on LinkedIn right now"
                hint="A few honest lines: what you post about, how often, and whether you comment on others' work."
                required
              >
                <Textarea
                  rows={4}
                  value={form.activity_summary}
                  onChange={set('activity_summary')}
                  required
                  data-testid="input-activity"
                  placeholder="I post maybe once a month, usually about building our product. I comment occasionally but rarely start conversations."
                />
              </Field>

              <Field label="In one sentence, what are you trying to attract or build?" required>
                <Input value={form.goal} onChange={set('goal')} required data-testid="input-goal" placeholder="Early design partners for our beta." />
              </Field>

              <div className="space-y-3 pt-2 border-t border-ink/10">
                <label className="flex items-start gap-3 text-sm text-ink-soft cursor-pointer">
                  <Checkbox
                    checked={form.consent_terms}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent_terms: !!v }))}
                    data-testid="checkbox-consent-terms"
                  />
                  <span>
                    I agree The Weave may process the info I've shared to generate my Aligned Network read, in line
                    with the <a className="underline-wave" href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>. I understand
                    this is AI-generated, a starting point, and not professional, legal or regulatory advice.
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm text-ink-soft cursor-pointer">
                  <Checkbox
                    checked={form.consent_marketing}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent_marketing: !!v }))}
                    data-testid="checkbox-consent-marketing"
                  />
                  <span>Optional — send me occasional notes from The Weave. Unsubscribe any time.</span>
                </label>
              </div>

              <div className="flex items-center gap-4 pt-2">
                <Button type="submit" size="lg" disabled={loading} data-testid="submit-diagnostic">
                  {loading ? 'Reading the signal…' : 'Get my Aligned Network read'} {!loading && <ArrowRight className="h-4 w-4" />}
                </Button>
                {loading && <span className="text-xs text-ink-mute">This takes ~15–30 seconds.</span>}
              </div>
            </form>
          </Reveal>
        </div>

        {report && <ReportCard report={report} reportRef={reportRef} />}
      </div>
    </section>
  );
};

const Field = ({ label, hint, required, children }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <Label className="text-[13px]">
        {label}{required && <span className="text-weave-red">*</span>}
      </Label>
      {hint && <span className="text-[11px] text-ink-mute">{hint}</span>}
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

/* ---------- Report ---------- */

const dimColor = {
  alignment: 'bg-weave-yellow',
  activity: 'bg-weave-teal',
  engagement: 'bg-weave-red',
  visibility: 'bg-ink',
};

const ReportCard = ({ report, reportRef }) => (
  <div ref={reportRef} className="mt-16 scroll-mt-24" data-testid="diagnostic-report">
    <div className="eyebrow text-weave-red">Your read</div>
    <h3 className="serif text-4xl md:text-5xl leading-tight mt-3">
      {report.overall_score}<span className="text-ink-mute text-2xl">/100 · {report.overall_label}</span>
    </h3>
    <p className="mt-4 max-w-2xl text-ink-soft leading-relaxed">{report.summary}</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
      {report.dimensions.map((d) => (
        <div key={d.id} className="grain-card p-6" data-testid={`dimension-${d.id}`}>
          <div className="flex items-center justify-between">
            <div className="eyebrow">{d.title}</div>
            <span className="text-xs text-ink-mute">{d.label}</span>
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="serif text-5xl leading-none">{d.score}</div>
            <div className="text-ink-mute text-sm">/100</div>
          </div>
          <div className="score-bar mt-4">
            <span style={{ width: `${d.score}%` }} className={dimColor[d.id] || 'bg-ink'} />
          </div>
          <dl className="mt-5 text-sm space-y-3">
            <div>
              <dt className="text-ink-mute text-xs uppercase tracking-wider">Strength</dt>
              <dd className="text-ink mt-1">{d.strength}</dd>
            </div>
            <div>
              <dt className="text-ink-mute text-xs uppercase tracking-wider">Gap</dt>
              <dd className="text-ink mt-1">{d.gap}</dd>
            </div>
            <div>
              <dt className="text-weave-red text-xs uppercase tracking-wider">Do next</dt>
              <dd className="text-ink mt-1">{d.action}</dd>
            </div>
          </dl>
        </div>
      ))}
    </div>

    <div className="mt-10 bg-ink text-cream-50 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
      <div className="max-w-2xl">
        <div className="eyebrow" style={{ color: '#f4b847' }}>Your signature move</div>
        <p className="serif text-2xl md:text-3xl mt-3 leading-snug">{report.signature_move}</p>
      </div>
      <div className="shrink-0">
        <a href={WEAVE_CLUB_URL} target="_blank" rel="noreferrer" className="btn-yellow" data-testid="report-join-cta">Join The 1% Club <ArrowRight className="h-4 w-4" /></a>
      </div>
    </div>
    <p className="mt-4 text-xs text-ink-mute">A copy of your read has been sent to your inbox.</p>
  </div>
);

/* ---------- Networker ---------- */

const DailySystem = () => {
  const steps = [
    { n: '01', t: 'Notice', m: '2 min', icon: Radar, c: 'bg-weave-yellow',
      d: 'Use the extension as your lens. Look for people who resonate, not perform. This is selection, not scrolling.' },
    { n: '02', t: 'Engage with intent', m: '5 min', icon: MessageSquareText, c: 'bg-weave-teal',
      d: 'Reflect → Add → Extend. Leave a trace of your thinking. Be remembered for your contribution, not your presence.' },
    { n: '03', t: 'Connect', m: '3 min', icon: Handshake, c: 'bg-weave-red',
      d: 'Personal. Relevant. Light. You\'re opening a door — not walking through it uninvited.' },
    { n: '04', t: 'Tag & track', m: 'new in v2', icon: Tag, c: 'bg-ink',
      d: 'Categorise: Future Collaborator · Peer · Mentor · Interesting Thinker. If it\'s not tracked, it won\'t compound.' },
    { n: '05', t: 'Nurture', m: '5 min', icon: Repeat, c: 'bg-weave-yellow',
      d: 'Within 7 days, re-engage with substance. Consistency builds familiarity. Familiarity builds trust.' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-5" data-testid="daily-system">
      {steps.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.07}>
          <div className="grain-card p-5 h-full">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center justify-center h-9 w-9 rounded-full text-ink ${s.c}`}>
                <s.icon className="h-4 w-4" />
              </span>
              <span className="text-[11px] text-ink-mute uppercase tracking-wider">{s.m}</span>
            </div>
            <div className="idx mt-4 text-weave-red">{s.n}</div>
            <h4 className="serif text-xl mt-2">{s.t}</h4>
            <p className="text-sm text-ink-soft mt-2 leading-relaxed">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

const FiveFiveFive = () => (
  <div className="bg-ink rounded-2xl p-8 md:p-12 text-cream-50" data-testid="five-five-five">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <div>
        <div className="eyebrow" style={{ color: '#f4b847' }}>The 5 · 5 · 5 rhythm</div>
        <h3 className="serif text-4xl md:text-5xl leading-tight mt-3">
          Small daily.<br />Compounding weekly.
        </h3>
      </div>
      <p className="text-sm text-cream-100/80 max-w-md leading-relaxed">
        Three moves a day. Fifteen minutes. Not networking — building a network that builds back.
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
      {[
        { n: '5', t: 'meaningful comments', d: 'Reflect → Add → Extend.' },
        { n: '5', t: 'intentional connections', d: 'Light, specific, remembered.' },
        { n: '5', t: 'tracked follow-ups', d: 'Within seven days. Always.' },
      ].map((x, i) => (
        <div key={i} className="border-t border-cream-50/20 pt-5">
          <div className="serif text-7xl md:text-8xl leading-none text-weave-yellow">{x.n}</div>
          <div className="mt-2 text-cream-50/90">{x.t}</div>
          <div className="mt-1 text-sm text-cream-50/60">{x.d}</div>
        </div>
      ))}
    </div>
  </div>
);

const Flywheel = () => {
  const nodes = [
    { t: 'Intentional engagement', d: 'You show up with thinking, not noise.', c: 'border-weave-yellow' },
    { t: 'Recognition', d: 'They start to notice you.', c: 'border-weave-teal' },
    { t: 'Relevance', d: 'They value your input.', c: 'border-weave-red' },
    { t: 'Conversation', d: 'DMs, calls, ideas.', c: 'border-ink' },
    { t: 'Contribution', d: 'You support, introduce, collaborate.', c: 'border-weave-yellow' },
  ];
  return (
    <div data-testid="flywheel">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {nodes.map((n, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className={`flywheel-node border-l-4 ${n.c}`}>
              <div className="eyebrow">0{i + 1}</div>
              <div className="serif text-xl mt-1">{n.t}</div>
              <div className="text-sm text-ink-soft mt-1">{n.d}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-6 text-sm text-ink-mute">
        Inner Circle &mdash; people who trust your thinking enough to act on it.
      </div>
    </div>
  );
};

const Networker = () => (
  <section id="networker" className="section-pad" data-testid="networker">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <Reveal className="md:col-span-7">
          <div className="eyebrow">The Weave Networker · Chrome extension</div>
          <h2 className="serif text-4xl md:text-6xl leading-[1.05] mt-4">
            The tool that makes 5&middot;5&middot;5 compound.
          </h2>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.1}>
          <p className="text-ink-soft leading-relaxed">
            Notice, engage, connect, tag, nurture. The Networker turns LinkedIn from a feed
            into a relationship system — so the work you put in actually compounds.
          </p>
        </Reveal>
      </div>

      <div className="mt-14">
        <DailySystem />
      </div>

      <div className="mt-20">
        <Reveal>
          <div className="eyebrow">From contact → inner circle</div>
          <h3 className="serif text-3xl md:text-4xl leading-tight mt-3 max-w-2xl">
            The flywheel that makes an introduction feel earned.
          </h3>
        </Reveal>
        <div className="mt-8">
          <Flywheel />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- The 5·5·5 practice (free with every read) ---------- */

const ThePractice = ({ onScanClick }) => (
  <section className="section-pad" data-testid="the-practice">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <Reveal className="md:col-span-7">
          <div className="eyebrow">Yours to use from tomorrow</div>
          <h2 className="serif text-4xl md:text-6xl leading-[1.05] mt-4">
            The 5&middot;5&middot;5 daily practice. Free with every read.
          </h2>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.08}>
          <p className="text-ink-soft leading-relaxed">
            The same lightweight ritual we use inside The Weave to turn LinkedIn from a feed into a network that builds back. Three small moves a day. Fifteen minutes. The starting strategy you walk away with — extension or no extension.
          </p>
          <button
            onClick={onScanClick}
            className="mt-5 inline-flex items-center gap-2 text-sm text-ink underline-wave"
            data-testid="practice-scan-link"
          >
            Get the read that comes with it <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Reveal>
      </div>
      <div className="mt-12">
        <Reveal>
          <FiveFiveFive />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- Free vs The 1% Club ---------- */

const BulletDark = ({ children }) => (
  <li className="flex items-start gap-2">
    <Check className="h-4 w-4 mt-0.5 text-weave-yellow shrink-0" />
    <span>{children}</span>
  </li>
);

const FreeVsClub = ({ onScanClick }) => (
  <section className="section-pad bg-cream-100/80 border-y border-ink/10" data-testid="free-vs-club">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <div className="eyebrow">Free vs The 1% Club</div>
        <h2 className="serif text-4xl md:text-6xl leading-[1.05] mt-3 max-w-3xl">
          Free is a taste. Members go further.
        </h2>
        <p className="mt-5 text-ink-soft max-w-2xl leading-relaxed">
          The Aligned Network read opens the door. The 1% Club is the room you walk into — a supportive space to invest in your business, your network and what comes next.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        <Reveal>
          <div className="grain-card p-7 md:p-9 h-full flex flex-col" data-testid="freeclub-free-card">
            <div className="eyebrow">Free · this page</div>
            <h3 className="serif text-3xl mt-3">The Aligned Network read</h3>
            <ul className="mt-6 space-y-3 text-sm text-ink-soft">
              <Bullet>AI appraisal across alignment, activity, engagement &amp; visibility</Bullet>
              <Bullet>One strength, one gap, one next move per dimension</Bullet>
              <Bullet>Your signature move — a single 14-day shift</Bullet>
              <Bullet>The 5&middot;5&middot;5 daily practice</Bullet>
              <Bullet>Sent to your inbox</Bullet>
            </ul>
            <div className="mt-auto pt-8">
              <Button size="lg" onClick={onScanClick} data-testid="freeclub-free-cta">
                Get my free read <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-2xl bg-ink text-cream-50 p-7 md:p-9 h-full flex flex-col" data-testid="freeclub-club-card">
            <div className="flex items-center justify-between">
              <div className="eyebrow" style={{ color: '#f4b847' }}>1% Club · £49 / month</div>
              <span className="text-[11px] uppercase tracking-wider text-cream-50/70">Recommended</span>
            </div>
            <h3 className="serif text-3xl mt-3">The Networker, and a place to grow.</h3>
            <ul className="mt-6 space-y-3 text-sm text-cream-100/85">
              <BulletDark>The Weave Networker — a relationship OS for LinkedIn (Chrome extension)</BulletDark>
              <BulletDark>A 15-minute set-up call once you&apos;re in</BulletDark>
              <BulletDark>Curated introductions, with context — you give and you receive</BulletDark>
              <BulletDark>Twice-yearly clinics on funding, network and what to build next</BulletDark>
              <BulletDark>Opportunity &middot; Capacity &middot; Network &middot; Cash — a genuine sounding board as you grow</BulletDark>
            </ul>
            <div className="mt-auto pt-8">
              <a
                href={WEAVE_CLUB_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-weave-yellow text-ink px-6 py-3.5 rounded-full font-semibold text-base hover:brightness-95 transition"
                data-testid="freeclub-join-cta"
              >
                Join The 1% Club <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      <p className="mt-8 text-xs text-ink-mute max-w-2xl">
        The 1% Club is open and supportive. Cancel any time. Members get the Networker through a 15-minute set-up call once you&apos;re in.
      </p>
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <Check className="h-4 w-4 mt-0.5 text-weave-teal shrink-0" />
    <span>{children}</span>
  </li>
);

/* ---------- Closing + footer ---------- */

const Closing = ({ onScanClick }) => (
  <section className="section-pad" data-testid="closing">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="max-w-3xl">
        <div className="eyebrow">The 1% Club</div>
        <p className="serif text-3xl md:text-5xl leading-[1.08] mt-4 text-ink">
          Not a closed room. A space to invest in yourself, your business, and the relationships that move it forward.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-10">
          <Button size="lg" onClick={onScanClick} data-testid="closing-scan-cta">Start with a free read</Button>
          <a
            href={WEAVE_CLUB_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-base font-medium h-14 px-8 border border-ink/20 bg-transparent text-ink hover:bg-ink/5 transition-colors"
            data-testid="closing-join-cta"
          >
            Join The 1% Club <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={WEAVE_LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-base font-medium h-14 px-7 bg-weave-teal text-white hover:brightness-105 transition"
            data-testid="closing-linkedin-cta"
          >
            <Linkedin className="h-4 w-4" /> Follow The Weave on LinkedIn
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-ink/10 pt-10 pb-16" data-testid="site-footer">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_aligned-network/artifacts/a37uk3vc_WEAVE-LOGO%202024.png"
            alt="The Weave"
            className="h-10 w-auto"
          />
          <p className="mt-4 text-sm text-ink-soft max-w-sm">
            A members' collective for the 1% of founders who build with intent. Opportunity. Capacity. Network. Cash.
          </p>
          <a
            href={WEAVE_LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-weave-teal transition-colors"
            data-testid="footer-linkedin-cta"
          >
            <Linkedin className="h-4 w-4" /> Follow The Weave on LinkedIn
          </a>
        </div>
        <div className="text-sm text-ink-mute space-y-2">
          <div className="flex gap-4 flex-wrap">
            <a className="underline-wave text-ink" href="https://www.wearetheweave.co.uk" target="_blank" rel="noreferrer">wearetheweave.co.uk</a>
            <a className="underline-wave text-ink" href="https://catch.wearetheweave.co.uk" target="_blank" rel="noreferrer">Catch the Wave</a>
            <a className="underline-wave text-ink" href="https://funding-hub.wearetheweave.co.uk" target="_blank" rel="noreferrer">Funding Fit</a>
          </div>
          <div className="pt-3">© {new Date().getFullYear()} The Weave · hello@wearetheweave.co.uk</div>
          <div className="text-xs">Diagnostic is AI-generated. A starting point, not professional advice.</div>
        </div>
      </div>
    </div>
  </footer>
);

/* ---------- Landing ---------- */

export default function Landing() {
  const formRef = useRef(null);
  const reportRef = useRef(null);
  const [report, setReport] = useState(null);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div data-testid="landing-page">
      <Nav onScanClick={scrollToForm} />
      <Hero onScanClick={scrollToForm} />
      <HowItWorks />
      <DiagnosticSection formRef={formRef} reportRef={reportRef} onReport={setReport} report={report} />
      <ThePractice onScanClick={scrollToForm} />
      <Networker />
      <FreeVsClub onScanClick={scrollToForm} />
      <Closing onScanClick={scrollToForm} />
      <Footer />
    </div>
  );
}
